import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { postOperatorNotes } from '../../../../../store';

const ZUG_CLIENT_ID = '537477000018971065';

// Convert limitations array to text format for storage
export const limitationsToText = (limitations, t) => {
  return limitations
    .filter((lim) => lim.checked)
    .map((lim) => {
      if (lim.custom) {
        return `${lim.title}${lim.description ? ': ' + lim.description : ''}`;
      }
      // Use translation keys for standard limitations
      return lim.title;
    })
    .join('\n');
};

const BASE_LIMITATIONS = [
  { id: 'size', titleKey: 'Limitations.size', descriptionKey: 'Limitations.size_description' },
  { id: 'pain', titleKey: 'Limitations.pain', descriptionKey: 'Limitations.pain_description' },
  {
    id: 'immobility',
    titleKey: 'Limitations.immobility',
    descriptionKey: 'Limitations.immobility_description',
  },
];

const CLIENT_LIMITATIONS = {
  [ZUG_CLIENT_ID]: [
    {
      id: 'pectus_excavatum',
      titleKey: 'Limitations.pectus_excavatum',
      descriptionKey: 'Limitations.pectus_excavatum_description',
    },
    {
      id: 'prominent_rib_cage_sternum',
      titleKey: 'Limitations.prominent_rib_cage_sternum',
      descriptionKey: 'Limitations.prominent_rib_cage_sternum_description',
    },
    { id: 'kyphosis', titleKey: 'Limitations.kyphosis', descriptionKey: 'Limitations.kyphosis_description' },
  ],
};

const toLimitation = (definition, t) => ({
  id: definition.id,
  title: t(definition.titleKey),
  description: definition.descriptionKey ? t(definition.descriptionKey) : '',
  checked: false,
});

// Parse text from store to limitations array (until new format is implemented)
export const textToLimitations = (text, client_id, t) => {
  const extraLimitations = CLIENT_LIMITATIONS[String(client_id || '').trim()] || [];
  const standardLimitations = [...BASE_LIMITATIONS, ...extraLimitations].map((definition) =>
    toLimitation(definition, t),
  );

  if (!text) return standardLimitations;

  const lines = text.split('\n').filter((line) => line.trim());
  const customLimitations = [];
  const matchedStandardIds = new Set();

  lines.forEach((line, index) => {
    const trimmedLine = line.trim().toLowerCase();
    let matchedStandard = false;

    // Check for standard limitations - match both title and description (case-insensitive)
    standardLimitations.forEach((lim) => {
      const titleLower = lim.title.toLowerCase();
      const descriptionLower = lim.description.toLowerCase();

      // Empty description matches every line via includes('')
      if (
        (titleLower && trimmedLine.includes(titleLower)) ||
        (descriptionLower && trimmedLine.includes(descriptionLower))
      ) {
        lim.checked = true;
        matchedStandardIds.add(lim.id);
        matchedStandard = true;
      }
    });

    // If line doesn't match any standard limitation, treat as custom
    if (!matchedStandard && trimmedLine) {
      const [title, ...descriptionParts] = line.trim().split(':');
      customLimitations.push({
        id: `custom_${Date.now()}_${index}`,
        title: title.trim(),
        description: descriptionParts.join(':').trim(),
        checked: true,
        custom: true,
      });
    }
  });

  return [...standardLimitations, ...customLimitations];
};

export const useLimitations = (open = true, onClose = null, autoSave = false) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const storeText = useSelector((state) => state.results.notes);
  const patient_id = useSelector((store) => store.results.patient_id);
  const client_id = useSelector((store) => store.global_settings.client_id);

  const [limitations, setLimitations] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);

  // Track initial limitations to detect changes
  const initialLimitationsRef = useRef([]);
  // Track auto-save timeout
  const autoSaveTimeoutRef = useRef(null);

  // Initialize limitations from store text
  const initializeLimitations = useCallback(() => {
    const parsed = textToLimitations(storeText || '', client_id, t);
    setLimitations(parsed);
    initialLimitationsRef.current = JSON.parse(JSON.stringify(parsed));
    setHasChanges(false);
  }, [storeText, client_id, t]);

  // Initialize when dialog opens or patient changes
  useEffect(() => {
    if (open) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
      initializeLimitations();
      setShowAddSection(false);
    }
  }, [open, patient_id, initializeLimitations]);

  // Check for changes whenever limitations change
  useEffect(() => {
    if (limitations.length > 0) {
      const currentText = limitationsToText(limitations, t);
      const initialText = limitationsToText(initialLimitationsRef.current, t);
      setHasChanges(currentText !== initialText);
    }
  }, [limitations, t]);

  // Handle save - convert limitations to text and save to store
  const handleSave = useCallback(async () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    const textToSave = limitationsToText(limitations, t);

    setIsSaving(true);
    try {
      await dispatch(postOperatorNotes(textToSave, t));
      initialLimitationsRef.current = JSON.parse(JSON.stringify(limitations));
      setHasChanges(false);
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving limitations:', error);
    } finally {
      setIsSaving(false);
    }
  }, [limitations, dispatch, t, onClose]);

  // Auto-save functionality for card view
  useEffect(() => {
    // Only auto-save if enabled and there are changes
    if (autoSave && hasChanges && !isSaving) {
      // Clear any existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      autoSaveTimeoutRef.current = setTimeout(() => {
        handleSave();
      }, 1000);

      return () => {
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
      };
    } else {
      // Clear timeout if no changes or auto-save disabled
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    }
  }, [hasChanges, autoSave, isSaving, handleSave]);

  // Handle toggle limitation
  const handleToggle = useCallback((id) => {
    setLimitations((prev) => prev.map((lim) => (lim.id === id ? { ...lim, checked: !lim.checked } : lim)));
  }, []);

  // Handle delete custom limitation
  const handleDelete = useCallback((id) => {
    setLimitations((prev) => prev.filter((lim) => lim.id !== id));
  }, []);

  // Handle add custom limitation
  const handleAdd = useCallback(() => {
    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) return;

    const newLim = {
      id: `custom_${Date.now()}`,
      title: trimmedTitle,
      description: newDescription.trim(),
      checked: true,
      custom: true,
    };

    setLimitations((prev) => [...prev, newLim]);
    setNewTitle('');
    setNewDescription('');
    setShowAddSection(false);
  }, [newTitle, newDescription]);

  // Handle toggle add section
  const handleToggleAddSection = useCallback(() => {
    setShowAddSection((prev) => !prev);
  }, []);

  // Handle cancel/close
  const handleClose = useCallback(() => {
    if (hasChanges) {
      initializeLimitations();
    }
    setShowAddSection(false);
    setNewTitle('');
    setNewDescription('');
    if (onClose) {
      onClose();
    }
  }, [hasChanges, initializeLimitations, onClose]);

  // Reset add form
  const resetAddForm = useCallback(() => {
    setNewTitle('');
    setNewDescription('');
    setShowAddSection(false);
  }, []);

  return {
    limitations,
    newTitle,
    newDescription,
    isSaving,
    hasChanges,
    showAddSection,
    setNewTitle,
    setNewDescription,
    handleToggle,
    handleDelete,
    handleAdd,
    handleSave,
    handleClose,
    handleToggleAddSection,
    resetAddForm,
  };
};
